import { Text } from "@/components/ui"
import { cn } from "@/lib/cn"
import { type RefObject, useState } from "react"
import {
  BULB_COUNT,
  storeInitials,
  BULB_RADIUS,
  FACE_RADIUS,
  HUB_RADIUS,
  RIM_RADIUS,
  WHEEL_BOX,
  WHEEL_CENTER,
  wheelArcPath,
  wheelPoint,
} from "./prize-wheel-geometry"
import { WHEEL_PALETTE } from "./prize-wheel-palette"

const VIEW_BOX = `0 0 ${WHEEL_BOX} ${WHEEL_BOX}`
const BULBS = Array.from({ length: BULB_COUNT }, (_, index) => ({
  id: `bulb-${index}`,
  ...wheelPoint(((index + 0.5) * 360) / BULB_COUNT, BULB_RADIUS),
}))
const POINTER = { width: 24, height: 37, top: -4, pivotY: 10 }
const HUB_SHARE = `${((HUB_RADIUS * 2) / WHEEL_BOX) * 100}%`
const { gold, bulb, ink, ivory, shade } = WHEEL_PALETTE

function GoldStops() {
  return (
    <>
      <stop offset="0" stopColor={gold.mid} />
      <stop offset="0.5" stopColor={gold.deep} />
      <stop offset="1" stopColor={gold.light} />
    </>
  )
}

/** The static frame: soft drop shadow, the champagne-gold rim with a highlight, and the warm bulbs the spin twinkles. */
export function WheelRim({ uid, bulbsRef }: { uid: string; bulbsRef: RefObject<SVGGElement> }) {
  return (
    <svg
      viewBox={VIEW_BOX}
      aria-hidden="true"
      className="absolute inset-0 h-full w-full drop-shadow-game-wheel"
    >
      <defs>
        <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="1" y2="1">
          <GoldStops />
        </linearGradient>
        <radialGradient id={`${uid}-bulb`} cx="0.4" cy="0.35" r="0.7">
          <stop offset="0" stopColor={bulb.core} />
          <stop offset="1" stopColor={bulb.glow} />
        </radialGradient>
        <radialGradient id={`${uid}-glow`}>
          <stop offset="0" stopColor={bulb.glow} stopOpacity="0.95" />
          <stop offset="1" stopColor={bulb.glow} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={WHEEL_CENTER} cy={WHEEL_CENTER} r={RIM_RADIUS} fill={`url(#${uid}-gold)`} />
      <circle cx={WHEEL_CENTER} cy={WHEEL_CENTER} r={RIM_RADIUS - 0.6} fill="none" stroke={gold.deep} strokeWidth={1.2} />
      <path
        d={wheelArcPath(-80, -10, RIM_RADIUS - 3.5)}
        fill="none"
        stroke={bulb.core}
        strokeOpacity={0.55}
        strokeWidth={2}
        strokeLinecap="round"
      />
      <g ref={bulbsRef}>
        {BULBS.map((light) => (
          <g key={light.id}>
            <circle cx={light.x} cy={light.y} r={7.5} fill={`url(#${uid}-glow)`} />
            <circle cx={light.x} cy={light.y} r={4.2} fill={gold.deep} />
            <circle cx={light.x} cy={light.y} r={3.1} fill={`url(#${uid}-bulb)`} />
          </g>
        ))}
      </g>
    </svg>
  )
}

/** The static lighting over the face: a soft inner shadow at the rim and the thin dark ring between face and rim. */
export function WheelFront({ uid }: { uid: string }) {
  return (
    <svg viewBox={VIEW_BOX} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full">
      <defs>
        <radialGradient id={`${uid}-shade`} gradientUnits="userSpaceOnUse" cx={WHEEL_CENTER} cy={WHEEL_CENTER} r={FACE_RADIUS}>
          <stop offset="0.9" stopColor={shade} stopOpacity="0" />
          <stop offset="1" stopColor={shade} stopOpacity="0.28" />
        </radialGradient>
      </defs>
      <circle cx={WHEEL_CENTER} cy={WHEEL_CENTER} r={FACE_RADIUS} fill={`url(#${uid}-shade)`} />
      <circle cx={WHEEL_CENTER} cy={WHEEL_CENTER} r={FACE_RADIUS + 1} fill="none" stroke={ink} strokeWidth={2.4} />
    </svg>
  )
}

/** The gold pointer at the top, pivoting on its ink-jeweled head so each peg can knock it aside. */
export function WheelPointer({ uid, pointerRef }: { uid: string; pointerRef: RefObject<SVGSVGElement> }) {
  return (
    <svg
      ref={pointerRef}
      viewBox={`0 0 ${POINTER.width} ${POINTER.height}`}
      aria-hidden="true"
      className="pointer-events-none absolute drop-shadow-game-pointer"
      style={{
        left: `${50 - (POINTER.width / WHEEL_BOX) * 50}%`,
        top: `${(POINTER.top / WHEEL_BOX) * 100}%`,
        width: `${(POINTER.width / WHEEL_BOX) * 100}%`,
        height: `${(POINTER.height / WHEEL_BOX) * 100}%`,
        transformOrigin: `50% ${(POINTER.pivotY / POINTER.height) * 100}%`,
      }}
    >
      <defs>
        <linearGradient id={`${uid}-pointer`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={gold.light} />
          <stop offset="0.55" stopColor={gold.mid} />
          <stop offset="1" stopColor={gold.deep} />
        </linearGradient>
      </defs>
      <path
        d="M12 36 L3.56 13.11 A9 9 0 1 1 20.44 13.11 Z"
        fill={`url(#${uid}-pointer)`}
        stroke={gold.deep}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <path d="M12 16 L12 32" stroke={bulb.core} strokeOpacity={0.6} strokeWidth={1} shapeRendering="crispEdges" vectorEffect="non-scaling-stroke" />
      <circle cx={12} cy={10} r={4.6} fill={gold.deep} />
      <circle cx={12} cy={10} r={3.3} fill={ink} />
      <circle cx={10.9} cy={8.9} r={1.1} fill={ivory} fillOpacity={0.9} />
    </svg>
  )
}

/** The glossy gold hub cap carrying the store logo, or the initials of the store in ink on gold when there is none or it fails. */
export function WheelHub({ logoUrl, storeName }: { logoUrl?: string | null; storeName: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const showLogo = Boolean(logoUrl) && failedUrl !== logoUrl
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-game-metal p-game-rim shadow-game-hub"
      style={{ width: HUB_SHARE, height: HUB_SHARE }}
    >
      <div className={cn("relative h-full w-full overflow-hidden rounded-full", showLogo ? "bg-game-ivory" : "bg-game-metal")}>
        {logoUrl && showLogo ? (
          <img
            src={logoUrl}
            alt=""
            className="h-full w-full object-cover"
            decoding="async"
            onError={() => setFailedUrl(logoUrl)}
          />
        ) : (
          <Text as="span" size="game-title" family="game-display" tracking="normal" tone="default" className="grid h-full w-full place-items-center">
            {storeInitials(storeName)}
          </Text>
        )}
        <span aria-hidden="true" className="absolute inset-0 rounded-full bg-game-gloss shadow-game-hub-gloss" />
      </div>
    </div>
  )
}
